import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  startDownloadCurrentModuleConfigExcel,
  startDownloadModuleConfigExcelErrors,
  startImportModuleConfigExcel
} from "../../app/actions/moduleConfigExcelActions";
import { isModuleConfigExcelImportEnabled } from "../../app/helpers/flowEngineConfigHelper";
import { startLoadingModuleConfigurationsPaginated } from "../../app/actions/moduleConfigurationActions";

const ID_FORM = "admin-module-configuration";

const ImportModuleConfigExcel = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configuration = useSelector((state) => state.configuration);
  const enabled = isModuleConfigExcelImportEnabled(configuration);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [acceptedValidation, setAcceptedValidation] = useState(false);
  const [busy, setBusy] = useState(false);

  const bag = result?.bagRowProcess || [];
  const allValid = useMemo(
    () => Boolean(result?.ok && result.totalRows > 0 && result.numberOfRowsInvalid === 0),
    [result]
  );
  const errors = bag.flatMap((row) => (row.stepsErrorsList || []).map((item) => ({
    rowNumber: row.rowNumber,
    ...item
  })));

  const handleValidate = async () => {
    if (!file) {
      Swal.fire(t("titles.error"), t(`${ID_FORM}.import.error.file`), "error");
      return;
    }
    setBusy(true);
    const body = await dispatch(startImportModuleConfigExcel(file, false));
    setBusy(false);
    setAcceptedValidation(false);
    setResult(body);
    if (!body.ok && !body.bagRowProcess) {
      Swal.fire(t("titles.error"), body.msg || t(`${ID_FORM}.import.error.generic`), "error");
    }
  };

  const handleApply = async () => {
    if (!allValid || !acceptedValidation || !file) {
      return;
    }
    const confirm = await Swal.fire({
      title: t(`${ID_FORM}.import.confirm.title`),
      text: t(`${ID_FORM}.import.confirm.text`),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t(`${ID_FORM}.import.confirm.apply`),
      cancelButtonText: t("titles.cancel")
    });
    if (!confirm.isConfirmed) {
      return;
    }
    setBusy(true);
    const body = await dispatch(startImportModuleConfigExcel(file, true));
    setBusy(false);
    setResult(body);
    if (body.ok && body.applied) {
      Swal.fire(t("titles.success"), t(`${ID_FORM}.import.success`), "success");
      dispatch(startLoadingModuleConfigurationsPaginated(0, 10));
      setFile(null);
      setAcceptedValidation(false);
      return;
    }
    Swal.fire(t("titles.error"), body.msg || t(`${ID_FORM}.import.error.generic`), "error");
  };

  if (!enabled) {
    return <div className="alert alert-secondary">{t(`${ID_FORM}.import.disabled`)}</div>;
  }

  return (
    <div className="container">
      <div className="alert alert-danger">
        <strong>{t(`${ID_FORM}.import.sensitive-title`)}</strong>
        <p className="mb-1 mt-2">{t(`${ID_FORM}.import.sensitive-body`)}</p>
        <p className="mb-0">{t(`${ID_FORM}.import.backup-recommend`)}</p>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => dispatch(startDownloadCurrentModuleConfigExcel())}
        >
          <i className="fa fa-download me-1" />
          {t(`${ID_FORM}.import.download-current`)}
        </button>
      </div>

      <p className="small text-muted">{t(`${ID_FORM}.import.howto`)}</p>

      <input
        type="file"
        accept=".xlsx"
        className="form-control"
        onChange={(event) => {
          setFile(event.target.files?.[0] || null);
          setResult(null);
          setAcceptedValidation(false);
        }}
      />

      {result && (
        <div className="mt-3 small">
          {t(`${ID_FORM}.import.summary`, {
            total: result.totalRows || 0,
            valid: result.numberOfRowsValid || 0,
            invalid: result.numberOfRowsInvalid || 0
          })}
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-3 border rounded p-2" style={{ maxHeight: 260, overflowY: "auto" }}>
          <div className="fw-semibold mb-2">{t(`${ID_FORM}.import.errors-title`)}</div>
          {errors.map((item, index) => (
            <div key={`${item.rowNumber}-${item.field}-${index}`} className="small text-danger">
              {t(`${ID_FORM}.import.error-row`, { row: item.rowNumber })} {item.field}: {item.message}
            </div>
          ))}
        </div>
      )}

      {bag.length > 0 && (
        <button
          type="button"
          className="btn btn-link btn-sm px-0 mt-2"
          onClick={() => dispatch(startDownloadModuleConfigExcelErrors(bag))}
        >
          {t(`${ID_FORM}.import.download-errors`)}
        </button>
      )}

      <div className="form-check mt-3">
        <input
          id="moduleconfig-excel-valid"
          type="checkbox"
          className="form-check-input"
          disabled={!allValid}
          checked={acceptedValidation}
          onChange={(event) => setAcceptedValidation(event.target.checked)}
        />
        <label className="form-check-label" htmlFor="moduleconfig-excel-valid">
          {t(`${ID_FORM}.import.checkbox`)}
        </label>
      </div>

      <div className="mt-3 d-flex gap-2">
        <button type="button" className="btn btn-outline-primary" disabled={busy || !file} onClick={handleValidate}>
          {t(`${ID_FORM}.import.validate`)}
        </button>
        <button
          type="button"
          className="btn btn-danger"
          disabled={busy || !allValid || !acceptedValidation}
          onClick={handleApply}
        >
          {t(`${ID_FORM}.import.apply`)}
        </button>
      </div>
    </div>
  );
};

export default ImportModuleConfigExcel;
